import React from 'react';
import { type Role, useAuthStore } from '@/store/auth';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Button } from './ui/button';
import { Icons } from './icons';

interface ProtectedRouteProps {
  allowedRoles?: Role[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
}) => {
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const context = useAuthStore(state => state.context);
  if (!user) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }
  
  const userRole = user.organisation?.role || user.workspace?.role;

  if (allowedRoles) {
    if (!userRole || !allowedRoles.includes(userRole)) {
      return (
        <div className="mt-40 bg-background flex items-center justify-center pr-4">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <Icons.warning className="h-10 w-10 text-destructive" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight">403</h1>
              <h2 className="text-xl font-semibold text-muted-foreground">Access Forbidden</h2>
              <p className="text-sm text-muted-foreground">
                You don't have permission to access this page. Please contact your administrator if you believe this is an error.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="w-full sm:w-auto"
              >
                Go Back
              </Button>
              <Button 
                variant="default"
                onClick={() => navigate('/dashboard/map')}
                className="w-full sm:w-auto"
              >
                Go to Map
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }

  if (context === 'organisation' && !user?.organisation?.verified && !location.pathname.startsWith('/verify')) {
    return <Navigate to="/verify" state={{ from: location }} replace />
  }

  if (context === 'organisation' && user?.organisation?.verified && location.pathname.startsWith('/verify')) {
    return <Navigate to="/dashboard/map" state={{ from: location }} replace />
  }

  return <Outlet />;
};

export default ProtectedRoute;